import React, { useState, useMemo, ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  Row,
  Table as TanstackTableInstance,
  HeaderGroup,
  Cell,
  RowSelectionState,
  FilterFn,
} from '@tanstack/react-table';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TableSkeletonLoader from '../SkeletonLoader/TableSkeletonLoader';

// Type definitions
interface DefaultSorted {
  dataField?: string;
  id?: string;
  order?: 'asc' | 'desc';
}

interface RowEvents<T> {
  onClick?: (event: React.MouseEvent<HTMLTableRowElement>, rowData: T, rowIndex: number) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLTableRowElement>, rowData: T, rowIndex: number) => void;
}

interface ExpandRow<T> {
  renderer?: (rowData: T) => ReactNode;
}

interface TanstackTableProps<T = Record<string, unknown>> {
  columns: ColumnDef<T>[];
  data?: T[] | undefined;
  selectRow?: boolean | undefined;
  defaultSorted?: DefaultSorted[] | undefined;
  rowEvents?: RowEvents<T> | undefined;
  expandRow?: ExpandRow<T> | undefined;
  customSearch?: ((filterValue: string, data: T[]) => T[]) | undefined;
  rowStyle?: React.CSSProperties | ((rowData: T, rowIndex: number) => React.CSSProperties) | undefined;
  globalFilter?: string | undefined;
  onGlobalFilterChange?: ((value: string) => void) | undefined;
  onRowSelectionChange?: ((selectedRowIds: (string | number)[]) => void) | undefined;
  pageSize?: number | undefined;
  showPagination?: boolean | undefined;
  enableSearch?: boolean | undefined;
  className?: string | undefined;
  loading?: boolean;
}

// Helper function to safely access properties
const safeGet = (obj: any, path: string): any => {
  try {
    return obj?.[path];
  } catch {
    return undefined;
  }
};

const DEFAULT_PAGE_SIZES = [10, 20, 30, 40, 50];

// Change the constraint to allow any type
const TanstackTable = <T extends Record<string, any> = Record<string, any>>({
  columns,
  data,
  selectRow,
  defaultSorted,
  rowEvents,
  expandRow,
  customSearch,
  rowStyle,
  globalFilter: externalGlobalFilter,
  onGlobalFilterChange,
  onRowSelectionChange,
  pageSize = 10,
  showPagination = true,
  enableSearch = true,
  className = '',
  loading = false,
}: TanstackTableProps<T>) => {
  const [internalGlobalFilter, setInternalGlobalFilter] = useState<string>('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Use external filter if provided, otherwise use internal
  const globalFilter = externalGlobalFilter !== undefined ? externalGlobalFilter : internalGlobalFilter;
  const setGlobalFilter = onGlobalFilterChange || setInternalGlobalFilter;

  // Prepare columns with optional selection and expand columns
  const tanstackColumns = useMemo((): ColumnDef<T>[] => {
    const baseColumns: ColumnDef<T>[] = [...columns];

    // Add selection column if selectRow is enabled
    if (selectRow) {
      baseColumns.unshift({
        id: 'select',
        header: ({ table }: { table: TanstackTableInstance<T> }) => {
          const isAllSelected = table.getIsAllRowsSelected();
          const isSomeSelected = table.getIsSomeRowsSelected();
          return (
            <Checkbox
              size='small'
              checked={isAllSelected}
              indeterminate={isSomeSelected && !isAllSelected}
              onChange={table.getToggleAllRowsSelectedHandler()}
              slotProps={{ input: { 'aria-label': 'Select all rows' } }}
            />
          );
        },
        cell: ({ row }: { row: Row<T> }) => (
          <Checkbox
            size='small'
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(event) => event.stopPropagation()}
            slotProps={{ input: { 'aria-label': 'Select row' } }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      });
    }

    // Add expand column if expandRow is enabled
    if (expandRow) {
      baseColumns.push({
        id: 'expand',
        header: ({ table }: { table: TanstackTableInstance<T> }) => {
          const hasExpandableRows = table.getRowModel().rows.some((row: Row<T>) => row.getCanExpand());
          return hasExpandableRows ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
              <ExpandMore fontSize='small' />
            </Box>
          ) : null;
        },
        cell: ({ row }: { row: Row<T> }) => {
          if (!row.getCanExpand()) return null;
          const expanded = row.getIsExpanded();
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconButton
                size='small'
                aria-label={expanded ? 'Collapse row' : 'Expand row'}
                aria-expanded={expanded}
                onClick={(event) => {
                  event.stopPropagation();
                  row.getToggleExpandedHandler()();
                }}
                sx={{ color: expanded ? 'info.main' : 'text.secondary' }}
              >
                {expanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
              </IconButton>
            </Box>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      });
    }

    return baseColumns;
  }, [columns, selectRow, expandRow]);

  // Global filter function
  const globalFilterFn: FilterFn<T> = (row: Row<T>, columnId: string, filterValue: string): boolean => {
    if (customSearch) {
      const result = customSearch(filterValue, [row.original]);
      return result.length > 0;
    }

    // Default search behavior
    if (!filterValue) return true;

    const searchTerms = filterValue.toLowerCase().split(/[ ,]+/).filter(Boolean);

    const rowValues = Object.entries(row.original as Record<string, unknown>)
      .filter(([key]) => key !== 'sites' && key !== 'changelog' && key !== 'variations')
      .map(([_, value]) => {
        if (typeof value === 'object' && value !== null) {
          return Object.values(value as Record<string, unknown>).join(' ');
        }
        return String(value);
      })
      .join(' ')
      .toLowerCase();

    return searchTerms.every((term) => rowValues.includes(term));
  };

  const table = useReactTable<T>({
    data: data || [],
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(enableSearch && { getFilteredRowModel: getFilteredRowModel() }),
    ...(expandRow && { getExpandedRowModel: getExpandedRowModel() }),
    ...(selectRow && {
      onRowSelectionChange: (updater) => {
        setRowSelection(updater);
        // Notify parent of selection changes
        if (onRowSelectionChange && data) {
          const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
          const selectedRowIds = Object.keys(newSelection)
            .map((index) => {
              const item = data[parseInt(index)];
              // Try to find an id property in various common formats
              return safeGet(item, 'id') || safeGet(item, '_id') || safeGet(item, 'key') || parseInt(index);
            })
            .filter((id): id is string | number => id !== undefined);
          onRowSelectionChange(selectedRowIds);
        }
      },
    }),
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn,
    state: {
      ...(enableSearch && { globalFilter }),
      ...(selectRow && { rowSelection }),
    },
    initialState: {
      pagination: {
        pageSize,
      },
      sorting: defaultSorted
        ? [
            {
              id: defaultSorted[0]?.dataField || defaultSorted[0]?.id || '',
              desc: defaultSorted[0]?.order === 'desc',
            },
          ]
        : [],
    },
    ...(expandRow && {
      getRowCanExpand: (row: Row<T>): boolean => {
        // Check if this row has expandable content
        if (expandRow.renderer) {
          // Make expansion logic more flexible - check for common expansion patterns
          const rowData = row.original;

          // Check if it's a primary variant with variations (for offers table)
          if (
            safeGet(rowData, 'isPrimaryVariant') &&
            Array.isArray(safeGet(rowData, 'variations')) &&
            safeGet(rowData, 'variations').length > 0
          ) {
            return true;
          }

          // Add other expansion conditions here as needed
          // For example, check for children, items, etc.
          if (Array.isArray(safeGet(rowData, 'children')) && safeGet(rowData, 'children').length > 0) {
            return true;
          }

          if (Array.isArray(safeGet(rowData, 'items')) && safeGet(rowData, 'items').length > 0) {
            return true;
          }
        }
        return false;
      },
    }),
  });

  // Show loading state
  if (loading) {
    return (
      <Box className={`tanstack-table ${className}`}>
        <TableSkeletonLoader columns={columns} selectRow={selectRow ?? false} expandRow={!!expandRow} rowCount={10} />
      </Box>
    );
  }

  const columnCount = table.getAllColumns().length;
  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const { pageIndex, pageSize: currentPageSize } = table.getState().pagination;
  // Keep a caller-supplied page size selectable, otherwise MUI warns about an out-of-range value.
  const pageSizeOptions = [...new Set([...DEFAULT_PAGE_SIZES, currentPageSize])].sort((a, b) => a - b);

  return (
    <Box className={`tanstack-table ${className}`}>
      <TableContainer>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());
                  return (
                    <TableCell
                      key={header.id}
                      sortDirection={sorted || false}
                      onClick={header.column.getToggleSortingHandler()}
                      sx={{
                        cursor: canSort ? 'pointer' : 'default',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        bgcolor: 'background.default',
                        fontWeight: 600,
                      }}
                    >
                      {canSort && content !== null ? (
                        <TableSortLabel active={Boolean(sorted)} direction={sorted || 'asc'}>
                          {content}
                        </TableSortLabel>
                      ) : (
                        content
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row: Row<T>) => (
              <React.Fragment key={row.id}>
                <TableRow
                  hover
                  sx={{
                    cursor: 'pointer',
                    verticalAlign: 'middle',
                    ...(rowStyle && typeof rowStyle === 'function' ? rowStyle(row.original, row.index) : rowStyle),
                  }}
                  onClick={(e: React.MouseEvent<HTMLTableRowElement>) => {
                    // A dialog opened from one of this row's cells is a React child of
                    // that cell, and React events cross portals through the render tree.
                    // Without this guard, closing such a dialog counts as a row click.
                    if (!e.currentTarget.contains(e.target as Node)) return;
                    if (rowEvents?.onClick) {
                      rowEvents.onClick(e, row.original, row.index);
                    }
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => {
                    if (!e.currentTarget.contains(e.target as Node)) return;
                    if (rowEvents?.onMouseEnter) {
                      rowEvents.onMouseEnter(e, row.original, row.index);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell: Cell<T, unknown>) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
                {/* Expanded row content */}
                {row.getIsExpanded() && expandRow?.renderer && (
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ p: 0, border: 0 }}>
                      {expandRow.renderer(row.original)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align='center' sx={{ border: 0 }}>
                  <Typography variant='body2' color='text.secondary' sx={{ py: 3 }}>
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          component='div'
          count={filteredRowCount}
          page={pageIndex}
          rowsPerPage={currentPageSize}
          rowsPerPageOptions={pageSizeOptions}
          onPageChange={(_event, newPage) => table.setPageIndex(newPage)}
          onRowsPerPageChange={(event) => table.setPageSize(Number(event.target.value))}
          showFirstButton
          showLastButton
          labelRowsPerPage='Show'
        />
      )}
    </Box>
  );
};

export default TanstackTable;
